import { Dialog } from '@servicetitan/design-system';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import {
  AddCustomerMutationVariables,
  CustomerAddInput,
} from '@api/generated/graphql';
import { Drawer } from '@components/Drawer';
import FormFooterButtons from '@components/Drawer/components/FormFooterButtons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContactSupportButton } from '@lib/hooks/useContactSupportButton';
import { inboxLocators } from '@locators/inbox';
import ToastModule from '@modules/ToastModule';
import { useCallback, useEffect, useState } from 'react';
import commonStrings from 'src/constants/strings';
import { SearchParams } from '../CustomerSearch/components/CustomerSearchBox/CustomerSearchBox';
import { CustomerBySearchType } from '../CustomerSearch/data/types';
import CreateCustomerForm from './components/CreateCustomerForm/CreateCustomerForm';
import { CreateCustomerFormValues } from './components/CreateCustomerForm/CreateCustomerFormDefinitions';
import strings from './components/CreateCustomerForm/strings';
import { createCustomerValidationSchema } from './components/CreateCustomerForm/validations';
import getCreateCustomerInitialValues from './getCreateCustomerInitialValues';
import { useAddCustomerMutation } from './hooks/useAddCustomerMutation';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import { linkCustomerToCall } from './linkCustomerToCall';

export interface CreateCustomerProps {
  onClose: VoidFunction;
  onSelectCustomer: (customer: Partial<CustomerBySearchType>) => void;
  open: boolean;
  tenantId: string;
  searchParams: SearchParams;
}

export function CreateCustomer({
  onClose,
  onSelectCustomer,
  open,
  tenantId,
  searchParams,
}: CreateCustomerProps) {
  const [retryFormValues, setRetryFormValues] =
    useState<CreateCustomerFormValues | null>(null);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [inProgress, setInProgress] = useState(false);
  const {
    [FeatureFlagKeys.featureLinkCallInMonolithToCustomerAndLocationWhenCreatingNewCustomer]:
      featureLinkCallInMonolithToCustomerAndLocationWhenCreatingNewCustomer,
  } = useFeatureFlags();

  const {
    addCustomer,
    reset: resetMutation,
    success,
  } = useAddCustomerMutation();

  const formMethods = useForm<CreateCustomerFormValues>({
    defaultValues: getCreateCustomerInitialValues(searchParams),
    resolver: zodResolver(createCustomerValidationSchema),
  });

  const contactSupportProps = useContactSupportButton();

  const {
    handleSubmit,
    formState: { isDirty, defaultValues },
    reset,
  } = formMethods;

  const onSubmit: SubmitHandler<CreateCustomerFormValues> = useCallback(
    async (formValues) => {
      const { billingCustomer, customer, type } = formValues;
      try {
        setInProgress(true);
        setRetryFormValues(formValues);

        const { name, address, contacts } = customer;
        const billingAddress = billingCustomer?.address;
        const billingName = billingCustomer?.name;
        const billingContacts = billingCustomer?.contacts;
        const isCustomerDifferentFromTenant = billingAddress && billingName;
        const billToCustomer = isCustomerDifferentFromTenant
          ? {
              name: billingName,
              address: billingAddress,
              contacts: billingContacts,
            }
          : undefined;
        const input: CustomerAddInput = {
          name,
          address: {
            street: address.street,
            unit: address.unit,
            city: address.city,
            state: address.state,
            zip: address.zip,
            country: address.country,
            latitude: parseFloat(address.latitude),
            longitude: parseFloat(address.longitude),
          },
          contacts,
          type,
          billToCustomer: !billToCustomer
            ? undefined
            : {
                name: billToCustomer.name,
                address: {
                  street: billToCustomer.address.street,
                  unit: billToCustomer.address.unit,
                  city: billToCustomer.address.city,
                  state: billToCustomer.address.state,
                  zip: billToCustomer.address.zip,
                  country: billToCustomer.address.country,
                  latitude: parseFloat(billToCustomer.address.latitude),
                  longitude: parseFloat(billToCustomer.address.longitude),
                },
                contacts: billToCustomer.contacts,
              },
        };
        const variables: AddCustomerMutationVariables = { input, tenantId };

        const { data } = await addCustomer(variables);
        const res = data?.addCustomer;
        const customerId = res?.id;
        if (customerId) {
          const customerName = res.name;
          if (
            featureLinkCallInMonolithToCustomerAndLocationWhenCreatingNewCustomer
          ) {
            await linkCustomerToCall({
              tenantId,
              customerId,
              locationId: res?.locations[0]?.id,
            });
          }
          onClose();
          onSelectCustomer({ id: customerId, name: customerName });
          ToastModule.showToast({
            onClose: () => resetMutation(),
            status: 'success',
            title: strings.successToast.title,
          });
        } else {
          console.error(
            'Failed to create customer. Full response:',
            JSON.stringify(res),
          );
          throw new Error('Failed to create customer');
        }
      } catch (error) {
        console.error('Error adding customer:', error);
        ToastModule.showToast({
          onClose: () => resetMutation(),
          status: 'critical',
          title: strings.errorToast.title,
          toastBody: strings.errorToast.message,
          primaryActionName: 'Try Again',
          onPrimaryActionClick: () =>
            retryFormValues && onSubmit(retryFormValues),
          ...contactSupportProps,
        });
      } finally {
        setInProgress(false);
      }
    },
    [
      tenantId,
      addCustomer,
      onClose,
      onSelectCustomer,
      resetMutation,
      retryFormValues,
      contactSupportProps,
      featureLinkCallInMonolithToCustomerAndLocationWhenCreatingNewCustomer,
    ],
  );

  const handleClose = useCallback(() => {
    const showToast = isDirty;
    if (inProgress) {
      return;
    }
    if (showToast) {
      setShowUnsavedChangesDialog(true);
    } else {
      onClose();
    }
  }, [isDirty, inProgress, onClose]);

  useEffect(() => {
    if (success && !inProgress) {
      onClose();
    }
  }, [success, inProgress, onClose]);

  return (
    <>
      <Drawer
        footer={
          <FormFooterButtons
            loading={inProgress}
            primaryLocator={
              inboxLocators.customerSearch.createFormFields.createSubmitBtn
            }
            onPrimaryClick={handleSubmit(onSubmit)}
            onSecondaryClick={handleClose}
            primaryText='Create Customer'
            secondaryText='Go Back'
          />
        }
        header='Create Customer'
        onClose={!inProgress ? handleClose : undefined}
        open={open}
      >
        <FormProvider {...formMethods}>
          <CreateCustomerForm onSubmit={handleSubmit(onSubmit)} />
        </FormProvider>
      </Drawer>

      {showUnsavedChangesDialog && (
        <Dialog
          negative
          onPrimaryActionClick={() => {
            setShowUnsavedChangesDialog(false);
            reset(defaultValues);
            onClose();
          }}
          onSecondaryActionClick={() => setShowUnsavedChangesDialog(false)}
          open={showUnsavedChangesDialog}
          primaryActionName={
            commonStrings.labels.dialog.exitWarning.primaryAction
          }
          secondaryActionName={
            commonStrings.labels.dialog.exitWarning.secondaryAction
          }
          status='warning'
          title={commonStrings.labels.dialog.exitWarning.title}
        >
          {commonStrings.labels.dialog.exitWarning.body}
        </Dialog>
      )}
    </>
  );
}
